import React, { useState, useEffect, useMemo } from 'react';
import { auditLogsAPI } from '../services/api';
import {
  History,
  Search,
  Loader2,
  User,
  FileText,
  Shield,
  Filter,
  Download,
  X,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  submit: 'bg-amber-100 text-amber-800',
  approve: 'bg-emerald-100 text-emerald-800',
  reject: 'bg-red-100 text-red-800',
  return: 'bg-orange-100 text-orange-800',
  login: 'bg-slate-100 text-slate-800',
};

const ENTITY_ICONS = {
  submission: FileText,
  driver: User,
  vehicle: FileText,
  sop: FileText,
  corrective_action: Shield,
  user: User,
  depot: FileText,
};

const AuditLogItem = ({ log }) => {
  const Icon = ENTITY_ICONS[log.entity_type] || FileText;
  const actionColor = ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-800';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge className={actionColor}>{(log.action || '').toUpperCase()}</Badge>
              <span className="text-sm font-medium text-slate-900">{log.entity_type}</span>
              {log.user_name && <span className="text-xs text-slate-500">by {log.user_name}</span>}
              <span className="text-xs text-slate-400 ml-auto">{formatDate(log.created_at)}</span>
            </div>
            {log.description && (
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{log.description}</p>
            )}
            {log.entity_id && (
              <p className="text-xs text-slate-500 font-mono mt-1">ID: {log.entity_id}</p>
            )}
            {log.changes && Object.keys(log.changes).length > 0 && (
              <div className="mt-2 p-2 bg-slate-50 rounded text-xs font-mono">
                <pre className="whitespace-pre-wrap break-words text-slate-700 max-h-40 overflow-auto">
                  {JSON.stringify(log.changes, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const [limit, setLimit] = useState(50);

  const entityTypes = useMemo(
    () => ['submission', 'driver', 'vehicle', 'sop', 'corrective_action', 'depot', 'user'],
    [],
  );
  const actionTypes = useMemo(
    () => ['create', 'update', 'delete', 'submit', 'approve', 'reject', 'return', 'login'],
    [],
  );

  // Build params (used by both list + export)
  const buildParams = () => {
    const params = { limit };
    if (entityFilter !== 'all') params.entity_type = entityFilter;
    if (actionFilter !== 'all') params.action = actionFilter;
    if (userFilter !== 'all') params.user_id = userFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (searchText.trim()) params.q = searchText.trim();
    return params;
  };

  // Fetch distinct users once
  useEffect(() => {
    auditLogsAPI
      .listUsers()
      .then((r) => setUsers(r.data || []))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const response = await auditLogsAPI.list(buildParams());
        if (!cancelled) setLogs(response.data || []);
      } catch (error) {
        console.error('Failed to fetch audit logs:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // small debounce so typing in search doesn't spam the backend
    const t = setTimeout(fetchLogs, searchText ? 350 : 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityFilter, actionFilter, userFilter, startDate, endDate, searchText, limit]);

  const clearFilters = () => {
    setEntityFilter('all');
    setActionFilter('all');
    setUserFilter('all');
    setStartDate('');
    setEndDate('');
    setSearchText('');
    setLimit(50);
  };

  const handleExport = async (fullHistory = false) => {
    setExporting(true);
    try {
      const params = fullHistory ? { full_history: 'true' } : buildParams();
      delete params.limit; // don't cap exports
      await auditLogsAPI.exportExcel(params);
      toast.success(fullHistory ? 'Full audit history exported' : 'Current view exported');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters =
    entityFilter !== 'all' ||
    actionFilter !== 'all' ||
    userFilter !== 'all' ||
    !!startDate ||
    !!endDate ||
    !!searchText;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            AUDIT LOG
          </h1>
          <p className="text-slate-500 mt-1">Track all system changes and user activities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport(false)}
            disabled={exporting || loading}
            data-testid="export-current-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export Current View
          </Button>
          <Button
            onClick={() => handleExport(true)}
            disabled={exporting}
            data-testid="export-full-history-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export Full History
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-slate-500 hover:text-slate-700"
                onClick={clearFilters}
                data-testid="clear-filters-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Clear all
              </Button>
            )}
            <div className="ml-auto text-sm text-slate-500">{logs.length} records shown</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Entity Type</Label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger data-testid="entity-filter">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Action</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="Action Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map((action) => (
                    <SelectItem key={action} value={action}>
                      {action.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">User</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger data-testid="user-filter">
                  <SelectValue placeholder="Any user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Record Limit</Label>
              <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                <SelectTrigger data-testid="limit-filter">
                  <SelectValue placeholder="Limit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 records</SelectItem>
                  <SelectItem value="50">50 records</SelectItem>
                  <SelectItem value="100">100 records</SelectItem>
                  <SelectItem value="200">200 records</SelectItem>
                  <SelectItem value="500">500 records</SelectItem>
                  <SelectItem value="1000">1000 records</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">From Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-filter"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">To Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-filter"
              />
            </div>

            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs text-slate-600">Search (description or entity ID)</Label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Type to search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-9"
                  data-testid="search-text-filter"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No audit logs found</h3>
          <p className="text-slate-500 mt-1">Try adjusting your filters or widening the date range.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log, idx) => (
            <AuditLogItem key={log.id || idx} log={log} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
